import { Link } from "react-router-dom"
import { SEO } from '@alascorporation/seo'
import { siteConfig } from '../config/seo.config'
import { useEffect, useState, useCallback } from "react"
import { redirectToSso } from "@alascorporation/api-client"
import api from "../lib/api"
import { ssoConfig } from "../lib/sso"
import {
  ArrowRight,
  Play,
  Users,
  Clock,
  ChevronRight,
} from "lucide-react"
import { programs, getProgramLabel } from "../config/schools"
import { getCourseCoverUrl } from "../lib/course-covers"

interface Course {
  id: string
  title: string
  thumbnail_url: string
  category: string
  slug?: string
  instructor_name?: string
  student_count?: number
  estimated_hours?: number
  difficulty_level?: string
}

const difficultyLabel: Record<string, string> = {
  beginner: "Básico",
  intermediate: "Intermedio",
  advanced: "Avanzado",
}

export default function HomePage() {
  const [featuredCourses, setFeaturedCourses] = useState<Course[]>([])

  const fetchFeatured = useCallback(async () => {
    try {
      const { data } = await api.get("/academy/courses?limit=9")
      setFeaturedCourses(data.data.courses.slice(0, 9))
    } catch (error) {
      console.error("Error fetching courses:", error)
    }
  }, [])

  useEffect(() => {
    const frame = window.requestAnimationFrame(() => {
      void fetchFeatured()
    })
    return () => window.cancelAnimationFrame(frame)
  }, [fetchFeatured])

  return (
    <div>
      <SEO
        config={siteConfig}
        title="Academy by ALAS | Aprende Cohetería, Satélites y Aeroespacial"
        description="Plataforma de aprendizaje STEM aeroespacial. Cursos de cohetería, construcción de satélites, aviación y exploración espacial. Aprende a tu ritmo con instructores certificados."
        path="/"
        keywords={['cursos cohetería online', 'educación aeroespacial Colombia', 'aprender satélites', 'STEM aeroespacial', 'academy satelab', 'cursos aviación Colombia']}
      />
      <section className="relative overflow-hidden py-20">
        <div className="pointer-events-none absolute -left-40 -top-40 h-96 w-96 rounded-full bg-primary/10 blur-3xl" />
        <div className="pointer-events-none absolute -right-40 top-20 h-80 w-80 rounded-full bg-sky/10 blur-3xl" />

        <div className="section-shell relative">
          <div className="mx-auto max-w-3xl text-center">
            <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-primary/25 bg-primary/10 px-4 py-1.5 text-xs font-medium text-primary">
              <span className="h-1.5 w-1.5 rounded-full bg-primary" />
              Formación STEM aeroespacial para Colombia y LATAM
            </div>

            <h1 className="text-4xl font-bold leading-tight tracking-tight text-foreground sm:text-5xl lg:text-6xl">
              Tu carrera espacial
              <span className="text-primary"> comienza aquí</span>
            </h1>

            <p className="mt-6 text-base leading-relaxed text-muted-foreground sm:text-lg">
              Aprende aviación, cohetería, satélites y exploración espacial con metodología práctica.
              Programas alineados a estándares internacionales STEAM.
            </p>

            <div className="mt-8 flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
              <button
                type="button"
                onClick={() => redirectToSso(ssoConfig, "/dashboard")}
                className="btn-platzi h-12 px-8 text-base"
              >
                Solicitar acceso
              </button>
              <Link
                to="/courses"
                className="flex h-12 items-center gap-2 rounded-lg border border-border px-8 text-base font-medium text-foreground transition-colors hover:bg-accent"
              >
                Ver cursos
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>

            <div className="mt-12 flex flex-wrap items-center justify-center gap-8 border-t border-border pt-8 text-center">
              {[
                { value: "7", label: "Programas" },
                { value: "1000+", label: "Estudiantes impactados" },
                { value: "40+", label: "Instituciones" },
                { value: "STEAM", label: "Metodologia" },
              ].map((stat) => (
                <div key={stat.label}>
                  <p className="text-2xl font-bold text-foreground">{stat.value}</p>
                  <p className="text-sm text-muted-foreground">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="bg-primary py-10">
        <div className="section-shell flex flex-col items-center justify-between gap-4 sm:flex-row">
          <div>
            <p className="text-lg font-bold text-primary-foreground">
              Ruta completa SateLab Academy
            </p>
            <p className="text-sm text-primary-foreground/80">
              Organiza tu aprendizaje por programas y proyectos aplicados.
            </p>
          </div>
          <button
            type="button"
            onClick={() => redirectToSso(ssoConfig, "/dashboard")}
            className="shrink-0 rounded-lg bg-primary-foreground px-6 py-3 text-sm font-bold text-primary transition-all hover:brightness-95"
          >
            Conocer rutas
          </button>
        </div>
      </section>

      <section className="py-16">
        <div className="section-shell">
          <div className="mb-8 flex items-end justify-between">
            <div>
              <h2 className="text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
                Aprende por programa académico
              </h2>
            </div>
            <Link
              to="/courses"
              className="hidden items-center gap-1 text-sm font-medium text-primary hover:underline sm:flex"
            >
              Ver todas <ChevronRight className="h-4 w-4" />
            </Link>
          </div>

          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {programs.map((program) => (
              <Link
                key={program.label}
                to={program.href}
                className="group flex items-center gap-4 rounded-lg border border-border bg-card p-4 transition-all hover:border-primary/40 hover:bg-accent/50"
              >
                <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-lg ${program.bg}`}>
                  <program.icon className={`h-5 w-5 ${program.color}`} />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-foreground">
                    {program.label}
                  </p>
                  <p className="mt-0.5 truncate text-xs text-muted-foreground">
                    {program.description}
                  </p>
                </div>
                <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground/50 transition-transform group-hover:translate-x-0.5 group-hover:text-primary" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-border py-16">
        <div className="section-shell">
          <div className="mb-8 flex items-end justify-between">
            <div>
              <h2 className="text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
                Cursos más tomados ahora
              </h2>
            </div>
            <Link
              to="/courses"
              className="hidden items-center gap-1 text-sm font-medium text-primary hover:underline sm:flex"
            >
              Ver catálogo <ChevronRight className="h-4 w-4" />
            </Link>
          </div>

          {featuredCourses.length === 0 ? (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="h-64 animate-pulse rounded-lg bg-muted" />
              ))}
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {featuredCourses.map((course) => (
                <Link
                  key={course.id}
                  to={`/courses/${course.slug ?? course.title.toLowerCase().replace(/\s+/g, "-")}`}
                  className="group overflow-hidden rounded-xl bg-card ring-1 ring-foreground/10 transition-all hover:ring-primary/30"
                >
                  <div className="relative aspect-video overflow-hidden bg-muted">
                    <img
                      src={getCourseCoverUrl(course)}
                      alt={course.title}
                      className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 flex items-center justify-center bg-black/0 transition-colors group-hover:bg-black/30">
                      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary opacity-0 shadow-lg transition-opacity group-hover:opacity-100">
                        <Play className="h-5 w-5 fill-primary-foreground text-primary-foreground" />
                      </div>
                    </div>
                    <span className="absolute left-3 top-3 rounded-full bg-primary px-2.5 py-0.5 text-xs font-bold text-primary-foreground">
                      Acceso por semillero
                    </span>
                  </div>

                  <div className="p-4">
                    <div className="mb-2 flex items-center gap-2">
                      {course.category && (
                        <span className="text-xs font-medium text-primary">
                          {getProgramLabel(course.category)}
                        </span>
                      )}
                      {course.category && course.difficulty_level && (
                        <span className="text-xs text-muted-foreground">.</span>
                      )}
                      {course.difficulty_level && (
                        <span className="text-xs text-muted-foreground">
                          {difficultyLabel[course.difficulty_level] || course.difficulty_level}
                        </span>
                      )}
                    </div>

                    <h3 className="line-clamp-2 text-sm font-semibold leading-snug text-foreground">
                      {course.title}
                    </h3>

                    {course.instructor_name && (
                      <p className="mt-1 text-xs text-muted-foreground">
                        {course.instructor_name}
                      </p>
                    )}

                    <div className="mt-3 flex items-center gap-3 text-xs text-muted-foreground">
                      {course.student_count != null && (
                        <span className="flex items-center gap-1">
                          <Users className="h-3 w-3" />
                          {course.student_count.toLocaleString()}
                        </span>
                      )}
                      {course.estimated_hours != null && (
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {course.estimated_hours}h
                        </span>
                      )}
                    </div>

                    <div className="mt-3 border-t border-border pt-3">
                      <span className="text-sm font-bold text-primary">
                        Ruta guiada por institucion
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}

          <div className="mt-8 text-center">
            <Link
              to="/courses"
              className="inline-flex items-center gap-2 rounded-lg border border-border px-6 py-3 text-sm font-medium text-foreground transition-colors hover:bg-accent"
            >
              Ver todos los cursos
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      <section className="border-t border-border bg-card py-16">
        <div className="section-shell text-center">
          <h2 className="text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
            ¿Listo para empezar?
          </h2>
          <p className="mt-3 text-muted-foreground">
            Únete a la comunidad que aprende ciencia y tecnología aeroespacial cada semana.
          </p>
          <div className="mt-6 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <button
              type="button"
              onClick={() => redirectToSso(ssoConfig, "/dashboard")}
              className="btn-platzi h-11 px-8"
            >
              Crear cuenta
            </button>
            <Link
              to="/courses"
              className="flex h-11 items-center rounded-lg border border-border px-8 text-sm font-medium text-foreground transition-colors hover:bg-accent"
            >
              Explorar cursos
            </Link>
          </div>
        </div>
      </section>
    </div>
  )
}
